import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { FloatingCtaProvider, useFloatingCtaState } from './floatingCtaContext';
import { FEATURE_MENU_GROUPS, getFeatures } from '../lib/features';
import { TALK_TO_SALES_URL } from '../lib/links';
import { FeatureIcon } from './FeatureIcon';
// Map top-level nav labels to their live routes.
// NOTE: Enterprise, Security and Resources/Blog were moved to the noindex
// /unlisted namespace, so they're intentionally NOT linked from the nav.
const NAV_ROUTES: Record<string, string> = {
  Pricing: '/pricing',
  ChatGPT: '/chatgpt',
  Integrations: '/integrations',
  'Become an Affiliate': 'https://partners.dub.co/getgiga/affiliate-program'
};
// Nav labels whose target is an EXTERNAL site — rendered as a real anchor that
// opens in a new tab rather than an internal router Link.
const EXTERNAL_NAV = new Set(['Become an Affiliate']);
// Labels shown in the home-page top nav.
const NAV_LABELS = ['Features', 'Integrations', 'Pricing', 'ChatGPT'];
export function Layout() {
  return <FloatingCtaProvider data-id="element-4013" data-mp="1rl0iw0anzem9">
      <LayoutInner data-id="element-4014" data-mp="1rmtgkq3auoi4" />
    </FloatingCtaProvider>;
}
function LayoutInner() {
  // While a section (e.g. the integration "plugs into your stack" beat) owns
  // its own primary CTA, it suppresses this global floating "Try Giga for free"
  // button so the two don't compete. It fades back in once that beat passes.
  const floatingCtaSuppressed = useFloatingCtaState();
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  // True when the floating mobile "Try Giga for free" CTA is sitting over an
  // ORANGE surface (the founder note, the footer, etc.) — the CTA is orange
  // too, so it would vanish against those. When that happens we flip the
  // button to a white treatment so it stays visible. Any orange section can
  // opt in by tagging its element with data-cta-invert; the footer is tagged
  // too.
  const [overOrange, setOverOrange] = useState(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [featureMenuDismissed, setFeatureMenuDismissed] = useState(false);
  const features = getFeatures();
  const featureGroups = FEATURE_MENU_GROUPS.map(group => ({
    ...group,
    items: group.slugs.map(slug => features.find(feature => feature.slug === slug)).filter(Boolean)
  }));
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  const toggleSection = (section: string) => setOpenSection(prev => prev === section ? null : section);
  // These routes open with a dark (#1C1510 / #1A1410) hero, so while the header
  // is still transparent (before the user scrolls) the nav must switch to a
  // light treatment — white links and a white logo — to stay legible. Once
  // scrolled, the header turns solid white and reverts to the dark-on-light
  // treatment.
  const darkHeroRoutes = ['/unlisted/compare/chatgpt', '/unlisted/security', '/demo', '/unlisted/team-workspace'];
  const isTeamWorkspace = location.pathname === '/unlisted/team-workspace';
  const isFeatureRoute = location.pathname.startsWith('/features/');
  const onDark = (darkHeroRoutes.includes(location.pathname) || isFeatureRoute) && !isScrolled;
  // Both full-screen home heroes own their primary action at the top of the
  // page, so the header uses a clear white Log in action until scrolling begins.
  const atHeroTop = (location.pathname === '/ai-coworker' || isTeamWorkspace) && !isScrolled;
  // /alt owns a sticky pillar nav of its own, and two stacked bars competing
  // for the top of the viewport is one too many — so there the site header
  // scrolls away with the page and the pillar nav takes the top edge.
  const headerScrollsAway = location.pathname === '/';
  // The home page opens on a full-bleed photographic plate that the header
  // floats over, so the header's actions need their dark-plate treatment
  // there — as they do on the other dark-hero routes.
  const onPlate = onDark || headerScrollsAway && !isScrolled;
  // The LINKS follow the same plate test as the actions beside them. Keyed to
  // `onDark` alone they stayed grey over the home page's photograph while the
  // three actions had already gone white — half the bar legible, half of it
  // sinking into the image.
  const navLinkClass = `text-sm font-semibold transition-colors ${onPlate ? 'text-white hover:text-[#F7C9AC]' : 'text-neutral-600 hover:text-[#2B211A]'}`;
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 20;
      setIsScrolled(scrolled);
      if (scrolled) {
        if (currentScrollY > lastScrollY.current + 5) {
          setScrollDirection('down');
          setMobileMenuOpen(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setScrollDirection('up');
        }
      } else {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Watch every orange surface (the footer + any section tagged with
  // data-cta-invert, e.g. the founder note): when one of them is under the
  // bottom band of the viewport — exactly where the floating mobile CTA sits —
  // flip the CTA to its white treatment so the orange button doesn't blend
  // into the orange background. The negative top rootMargin shrinks the
  // observed region to just the bottom ~12% strip, so the flip happens only
  // while the CTA truly overlaps the orange surface (not the moment any part
  // of a tall orange section first peeks into view). Re-runs on route change
  // so it picks up the orange sections present on the current page.
  useEffect(() => {
    const surfaces = new Set<Element>();
    document.querySelectorAll('[data-cta-invert]').forEach(el => surfaces.add(el));
    if (footerRef.current) surfaces.add(footerRef.current);
    if (surfaces.size === 0) {
      setOverOrange(false);
      return;
    }
    const active = new Set<Element>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) active.add(entry.target);else active.delete(entry.target);
      });
      setOverOrange(active.size > 0);
    }, {
      // Only the bottom strip of the viewport counts — that's where the
      // floating CTA lives.
      rootMargin: '-88% 0px 0px 0px',
      threshold: 0
    });
    surfaces.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-4015" data-mp="1nb76dmoehgkd">
      <header className={`${headerScrollsAway ? 'absolute' : 'fixed'} top-0 w-full z-50 translate-y-0 transition-[background-color,padding,box-shadow] duration-300 ${isScrolled && !headerScrollsAway ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-4016" data-mp="18d4zb2i5ucyd">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-4017" data-mp="14dmdm4iilxuh">
          <Link to="/" aria-label="Giga home" className="flex items-center gap-2 group" data-id="element-4018" data-mp="pohv0m7t5rlu">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className={`h-7 w-7 object-contain group-hover:scale-105 transition-transform ${onPlate ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-4019" data-mp="unbb8mehbwpp" />

            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className={`h-6 object-contain transition-[filter] ${onPlate ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-4020" data-mp="112tlq448du31" />
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-id="element-4021" data-mp="m62krqewsabb">
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="relative group py-2" onMouseLeave={() => setFeatureMenuDismissed(false)} data-id="element-4022" data-mp="i6tr0ljvccfr">
                  <button type="button" className={`${navLinkClass} inline-flex items-center gap-1`} aria-haspopup="menu" onFocus={() => setFeatureMenuDismissed(false)} data-id="element-4023" data-mp="8exh8d8f6xvp">Features <ChevronDown size={14} data-id="element-4024" data-mp="3he2lc7t8j38" /></button>
                  <div className={`invisible absolute left-1/2 top-full w-[760px] -translate-x-1/2 translate-y-2 rounded-2xl border border-neutral-200 bg-white p-4 opacity-0 shadow-2xl transition-all duration-150 ${featureMenuDismissed ? '' : 'group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100'}`} data-id="element-4025" data-mp="3ulvxdu64d1n3">
                    <div className="grid grid-cols-3 gap-4" role="menu" data-id="element-4026" data-mp="2f2wp4tyb7mv2">
                      {featureGroups.map(group => <div key={group.key} data-id="element-4027" data-mp="2k2j4b8xp7d6i">
                        <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400" data-id="element-4028" data-mp="2k06niz3of8v1">{group.label}</p>
                        <div className="space-y-0.5" data-id="element-4029" data-mp="1rdc3u4eoodim">
                          {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} role="menuitem" onClick={() => setFeatureMenuDismissed(true)} className="flex items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-[#FFF4EC] transition-colors" data-id="element-4030" data-mp="1wcoocii318i1">
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EA580C]/10 text-[#EA580C]" data-id="element-4031" data-mp="13nhs5q63ouzy"><FeatureIcon name={feature.icon} size={16} data-id="element-4032" data-mp="18l9lmome8p69" /></span>
                            <span className="min-w-0 text-sm font-semibold leading-tight text-[#2B211A]" data-id="element-4033" data-mp="isueq6u7884l">{feature.menuLabel || feature.name}</span>
                          </Link>)}
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className={navLinkClass} data-id="element-4034" data-mp="xq4x8059ee9t">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className={`${navLinkClass} inline-flex items-center gap-1.5`} data-id="element-4035" data-mp="51fujwok20vy">
                  {label}{label === 'ChatGPT' && <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${onPlate ? 'bg-[#EA580C]/25 text-[#FDBA74]' : 'bg-[#EA580C]/10 text-[#EA580C]'}`} data-id="element-4036" data-mp="9z8z6la7mmu4">New</span>}
                </Link>)}
          </nav>

          <div className="flex items-center gap-3 md:gap-4" data-id="element-4037" data-mp="3igc5z355k279">
            {/* Three actions in ascending commitment: a plain text Log in, an
             outlined Talk to sales, and one filled Get started. No
             scroll-dependent swap — the same three show at every scroll
             position, so the reader never loses the action they were
             reaching for. Only the TREATMENT changes: over the home
             photograph the two visible controls take their dark-plate
             versions so they stay legible. */}
            <WaitlistCTA analyticsLabel="nav-log-in" spinnerClassName="text-[#2B211A]" className={`hidden md:block text-sm font-semibold transition-colors ${onPlate ? 'text-white hover:text-[#F7C9AC]' : 'text-neutral-600 hover:text-[#2B211A]'}`} data-id="element-4038" data-mp="1gog8h9u267g2">
              Log in
            </WaitlistCTA>
            <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className={`hidden md:block rounded-full border px-5 py-2.5 text-sm font-bold transition-colors ${onPlate ? 'border-white/50 text-white hover:bg-white/10' : 'border-[#E7E0D4] text-[#2B211A] hover:border-[#D9CFBE] hover:bg-[#FFF8F3]'}`} data-id="element-4039" data-mp="12xbcchpwfxah">
              Talk to sales
            </a>
            <WaitlistCTA analyticsLabel="nav-try-giga" spinnerClassName={onPlate ? 'text-[#2B211A]' : 'text-white'} className={`hidden md:block px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm ${onPlate ? 'bg-white text-[#2B211A] hover:bg-[#F5EFE7]' : 'bg-[#EA580C] hover:bg-[#C2410C] text-white'}`} data-id="element-4040" data-mp="o9bdqtd5d0jb">
              Get started
            </WaitlistCTA>

            {/* Mobile control: hamburger, plus a compact "Try for free" CTA.
             The header is only revealed while scrolling UP (it slides away on
             scroll-down), so showing the CTA here gives the user a quick
             conversion tap right next to the menu when they scroll back up.
             Hidden at the very top of the hero, where the hero already shows
             its own primary CTA. */}
            <div className="md:hidden flex items-center gap-2" data-id="element-4041" data-mp="1pqag3k74e106">
              {isScrolled && <WaitlistCTA analyticsLabel="mobile-try-free" className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-bold transition-colors shadow-sm whitespace-nowrap" data-id="element-4042" data-mp="3ag678k9p0gu5">
                  Try for free
                </WaitlistCTA>}
              <button className={`p-2 -mr-2 transition-colors ${onPlate ? 'text-white' : 'text-[#2B211A]'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} data-id="element-4043" data-mp="3k8bvbiwtqe42">
                {mobileMenuOpen ? <X size={24} data-id="element-4044" data-mp="2mj0eqpgtclqh" /> : <Menu size={24} data-id="element-4045" data-mp="2mjk06g5o9s99" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Compact */}
        {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-neutral-200 shadow-lg pb-3 px-5 flex flex-col gap-1.5 z-50 max-h-[80vh] overflow-y-auto" data-id="element-4046" data-mp="2wgy3de2l4kjm">
            {/* The same three actions, stacked to match their weight: the two
             lighter ones share a row, and the one filled control takes the
             full width beneath them. */}
            <div className="sticky top-0 -mx-5 px-5 pt-3 pb-3 bg-white border-b border-neutral-100 flex flex-col gap-2 z-10" data-id="element-4047" data-mp="2hh2nm4knco8q">
              <div className="flex gap-3" data-id="element-4048" data-mp="2qewhz9yup7t9">
                {/* Login is waitlisted for now — opens the waitlist instead of
                 the (preserved) /login page. */}
                <WaitlistCTA analyticsLabel="mobile-menu-log-in" spinnerClassName="text-[#2B211A]" className="flex-1 text-center bg-white text-[#2B211A] border border-neutral-200 px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-4049" data-mp="2vc4rkm5xo3iw">
                  Log in
                </WaitlistCTA>
                <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="flex-1 text-center bg-white text-[#2B211A] border border-neutral-200 px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-4050" data-mp="3a5hx6bl0eeco">
                  Talk to sales
                </a>
              </div>
              <WaitlistCTA analyticsLabel="mobile-menu-try-giga" className="w-full text-center bg-[#EA580C] text-white px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-4051" data-mp="3o3fzgim22kbt">
                Get started
              </WaitlistCTA>
            </div>
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="border-b border-neutral-100" data-id="element-4052" data-mp="3o977kc1crxd3">
                <button type="button" onClick={() => toggleSection('features')} className="w-full py-2 flex items-center justify-between text-base font-medium text-[#2B211A]" aria-expanded={openSection === 'features'} data-id="element-4053" data-mp="3k8di3e89iai3">
                  Features <ChevronDown size={17} className={`transition-transform ${openSection === 'features' ? 'rotate-180' : ''}`} data-id="element-4054" data-mp="ftvtjbka1wce" />
                </button>
                {openSection === 'features' && <div className="space-y-3 pb-3" data-id="element-4055" data-mp="700lnvrrtrt2">
                  {featureGroups.map(group => <div key={group.key} data-id="element-4056" data-mp="13iydiq2dw1a2">
                    <p className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-4057" data-mp="13kswvzy0blc1">{group.label}</p>
                    <div className="grid grid-cols-2 gap-1" data-id="element-4058" data-mp="2t03lq2cfwc4b">
                      {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-[#655B54] hover:bg-[#FFF4EC] hover:text-[#2B211A]" data-id="element-4059" data-mp="2xyna09ei9zzu">
                        <FeatureIcon name={feature.icon} size={15} className="shrink-0 text-[#EA580C]" data-id="element-4060" data-mp="2o8lhn5f4dvf4" /> <span data-id="element-4061" data-mp="2a9c7onhibxjl">{feature.menuLabel || feature.name}</span>
                      </Link>)}
                    </div>
                  </div>)}
                </div>}
              </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2" data-id="element-4062" data-mp="20behhug37qoc">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2 flex items-center gap-2" data-id="element-4063" data-mp="3uq8qzrdpvnbu">
                  {label}{label === 'ChatGPT' && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EA580C]/10 text-[#EA580C]" data-id="element-4064" data-mp="1o1ovukfhycei">New</span>}
                </Link>)}
          </div>}
      </header>

      <main className="flex-1" data-id="element-4065" data-mp="1xsb4fuosq2np">
        <Outlet data-id="element-4066" data-mp="1xsjjnt8e5k22" />
      </main>

      {/* Global floating "Try Giga for free" CTA — mobile only. Gives the user
                                    a persistent conversion tap as they scroll, even while the header is slid
                                    away on scroll-down. Hidden at the very top of the hero (where the hero
                                    owns its own primary CTA) and while an in-page section suppresses it.
                                    Over the orange footer it switches to a white treatment so it stays
                                    visible. */}
      <AnimatePresence data-id="element-4067" data-mp="mdntba0dtu5o">
        {isScrolled && !atHeroTop && !floatingCtaSuppressed ? <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="md:hidden fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 pointer-events-none" data-id="element-4068" data-mp="he1gv2sdohxx">
            <WaitlistCTA analyticsLabel="floating-cta" spinnerClassName={overOrange ? 'text-[#EA580C]' : 'text-white'} className={['pointer-events-auto inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-bold shadow-lg transition-colors', overOrange ? 'bg-white text-[#EA580C] shadow-black/20' : 'bg-[#EA580C] text-white shadow-[#EA580C]/30'].join(' ')} data-id="element-4069" data-mp="a7nwpwav8jtj">
              Get started
            </WaitlistCTA>
          </motion.div> : null}
      </AnimatePresence>

      <footer ref={footerRef} data-cta-invert="true" className="relative overflow-hidden bg-[#2B211A] text-sm text-white/80 pt-20 pb-40 md:pb-56" data-id="element-4070" data-mp="xx01mjz5u5q1">
        {/* PHOTOGRAPHIC GROUND. A dusk ridgeline with one band of bright light
         at the horizon. Anchored to the BOTTOM (not centred) so the ridge sits
         under the sitemap and the open sky carries the links — default centring
         puts the link text straight over the brightest part of the frame.
         No overall scrim: the plate is already near-black, so a wash would only
         flatten it. The short gradient at the top edge exists purely to keep
         the seam with the cream section above from reading as a hard cut. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-id="element-4071" data-mp="1zojfbm3sgpim">
          <img src="https://cdn.magicpatterns.com/uploads/oezJmNYbrxjRHbMnorXSyL/hf_20260903_082104_b928195c-1db7-4b4e-a352-02eb71cf62c1_4.png" alt="" className="size-full object-cover object-bottom" data-id="element-4072" data-mp="1upgb23vmunbp" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#2B211A] to-transparent" data-id="element-4073" data-mp="3p37qxl4k23fl" />
        </div>

        {/* Subtle grid texture, over the plate. */}
        <div className="dot-matrix opacity-[0.12]" aria-hidden="true" data-id="element-4074" data-mp="ftl6b6di99hk" />

        {/* Oversized ghosted brand wordmark — sits in the lower band of the
         footer (below the sitemap), gently bleeding off the bottom edge. */}
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-wordmark.svg" alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 -bottom-[2vw] mx-auto w-[80%] max-w-none object-contain opacity-[0.12] [filter:brightness(0)_invert(1)]" data-id="element-4075" data-mp="6300axvi52om" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4076" data-mp="3jal67jx96du8">
          {/* Sitemap. The three feature groups used to stack inside a single
           "Features" column, which ran ~17 links deep and left the other
           three columns empty for most of the footer's height. Each group is
           now its own column, so the row reads as five balanced lists. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 md:gap-8 pb-16 border-b border-white/20" data-id="element-4077" data-mp="1ciarb5kfii7r">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1" data-id="element-4078" data-mp="1p1eqt04vz2gt">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-7 object-contain mb-4 [filter:brightness(0)_invert(1)]" data-id="element-4079" data-mp="1tsxtyouamkhi" />

              <p className="text-white/70 leading-relaxed max-w-xs" data-id="element-4080" data-mp="12430mveyy5df">
                Everyone's new favorite colleague.
              </p>
            </div>

            {/* One column per feature group, so no single list runs long. */}
            {featureGroups.map(group => <div key={group.key} data-id="element-4081" data-mp="1g2b3fjyos0wi">
                <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-4082" data-mp="1g3cj61qndbkk">
                  {group.label}
                </h3>
                <ul className="space-y-2.5" data-id="element-4083" data-mp="2rgezcvou3z47">
                  {group.items.map(feature => feature && <li key={feature.slug} data-id="element-4084" data-mp="21ssr8kypzoq7">
                      <Link to={`/features/${feature.slug}`} className="group/footer inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-id="element-4085" data-mp="21rhliflps6wg">
                        <FeatureIcon name={feature.icon} size={14} className="shrink-0 text-white/55 transition-colors group-hover/footer:text-white" data-id="element-4086" data-mp="2bri94dqm996g" />
                        <span data-id="element-4087" data-mp="26q2mwicpcwmh">{feature.menuLabel || feature.name}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>)}

            {/* Product and Legal share the last column — together they are
             about the same depth as one feature group. */}
            <div className="space-y-8" data-id="element-4088" data-mp="1suh3qrt5h71z">
              {[{
              title: 'Product',
              links: [{
                label: 'Pricing',
                to: '/pricing'
              }, {
                label: 'Developers',
                to: '/developers'
              }]
            }, {
              title: 'Legal',
              links: [{
                label: 'Support',
                to: '/support'
              }, {
                label: 'Privacy',
                to: '/privacy'
              }, {
                label: 'Terms',
                to: '/terms'
              }, {
                label: 'Vulnerability Disclosure',
                to: '/vulnerability-disclosure'
              }]
            }].map(col => <div key={col.title} data-id="element-4089" data-mp="3pqboz8bgxc63">
                  <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-4090" data-mp="3prd0tf0b62ov">
                    {col.title}
                  </h3>
                  <ul className="space-y-2.5" data-id="element-4091" data-mp="3gqolmgz6hwae">
                    {col.links.map(link => <li key={link.label} data-id="element-4092" data-mp="3gshtfzlj2mjz">
                        <Link to={link.to} className="text-white/80 transition-colors hover:text-white" data-id="element-4093" data-mp="3kqyzsgx4jt0c">
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-4094" data-mp="2pkrni0q4y126">
            <div className="text-white/60" data-id="element-4095" data-mp="2kryl3ji534t9">
              © {new Date().getFullYear()} Tonic Labs Limited. All rights reserved.
            </div>
            <div className="flex gap-6" data-id="element-4096" data-mp="2frtz5dygphvn">
              {[{
              label: 'Twitter',
              href: 'https://x.com/askgiga'
            }].map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" data-id="element-4097" data-mp="38hj4u4m4a63l">
                  {social.label}
                </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}